import { useCallback, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { customerPermissionsConfigureExperience } from '../../experiences';
import { postRequestSecurity } from '../rest/request-security';
import type {
	RequestSecurityRequestBody,
	SecuritySettingsResponse,
	SecuritySettings,
} from '../types';
import { transformSource } from './util';

type ServiceData = {
	loading: boolean;
	data: SecuritySettings;
	error: string | undefined;
};
type ServiceActions = {
	saveRequestSecurity: (
		settings: RequestSecurityRequestBody,
		analyticsEvent: UIAnalyticsEvent,
	) => Promise<void>;
};

type RequestSecurityProviderProps = {
	projectKey: ProjectKey;
	source: SecuritySettingsResponse;
	onResourceUpdate?: (settings: SecuritySettingsResponse) => void;
};

export const useRequestSecuritySettings = ({
	projectKey,
	source,
	onResourceUpdate,
}: RequestSecurityProviderProps): [ServiceData, ServiceActions] => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [data, setData] = useState<SecuritySettings>(transformSource(source));

	const saveRequestSecurity = useCallback(
		async (requestBody: RequestSecurityRequestBody, analyticsEvent: UIAnalyticsEvent) => {
			try {
				setLoading(true);
				setError(undefined);
				customerPermissionsConfigureExperience.start();

				const response = await postRequestSecurity(projectKey, requestBody);
				setData(transformSource(response));
				if (onResourceUpdate !== undefined) {
					onResourceUpdate(response);
				}

				setLoading(false);
				customerPermissionsConfigureExperience.success();
				// https://data-portal.internal.atlassian.com/analytics/registry/34076
				fireTrackAnalytics(analyticsEvent, 'customerPermissionsSettings saved');
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				if (!isClientFetchError(err)) {
					// https://data-portal.internal.atlassian.com/analytics/registry/19568
					fireErrorAnalytics({
						error: err,
						meta: {
							id: 'updateRequestSecurity',
							packageName: 'jiraServicedeskCustomerPermissions',
							teamName: 'jsd-flux-capacitors',
						},
						sendToPrivacyUnsafeSplunk: true,
					});
					customerPermissionsConfigureExperience.failure();
				} else {
					customerPermissionsConfigureExperience.abort();
				}
				setError(err.message);
				setLoading(false);
			}
		},
		[onResourceUpdate, projectKey],
	);

	return [{ data, loading, error }, { saveRequestSecurity }];
};
